// src/components/analytics/Analytics.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Analytics — "TelcoFair Glass Workspace" with Firebase Data
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Tooltip,
    Stack,
    Tabs,
    Tab,
    CircularProgress,
    TablePagination,
    Menu,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    MenuList,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    Event as EventIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Analytics as AnalyticsIcon,
    Assessment as AssessmentIcon,
    Add as AddIcon,
    FiberManualRecord as LiveIcon,
    Money as MoneyIcon,
    Visibility as VisibilityIcon,
    Share as ShareIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Speed as SpeedIcon,
    Dashboard as DashboardIcon,
    PictureAsPdf as PdfIcon,
    Description as DescriptionIcon,
    TableChart as TableChartIcon,
    RocketLaunch as RocketIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    orderBy,
    onSnapshot,
    limit,
} from 'firebase/firestore';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
} from 'recharts';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ─────────────────────────────────────────────────────────────────────────────
// 1 · DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────

const palette = {
    canvas: '#F2F7F9',
    canvasAlt: '#F7FAFC',
    paper: 'rgba(255,255,255,0.88)',
    paperSolid: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#425469',
    inkLight: '#607287',
    inkMuted: '#8494A4',
    hairline: 'rgba(31,62,82,0.09)',
    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    pine: '#2E8B69',
    pineSoft: '#4CA17F',
    gold: '#D78B28',
    goldLight: '#E2A54D',
    teal: '#268AA3',
    tealLight: '#5EADC0',
    purple: '#8D5AC8',
    purpleLight: '#A97AD9',
    navy: '#315E9E',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    admin: '#7557C7',
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

// ─────────────────────────────────────────────────────────────────────────────
// 2 · LIQUID GLASS FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

const liquidGlass = (accent = palette.rust, intensity = 0.12, elevation = 'md') => {
    const elevations = {
        xs: `0 1px 2px ${rgba(palette.ink, 0.04)}, 0 1px 4px ${rgba(palette.ink, 0.04)}`,
        sm: `0 2px 4px ${rgba(palette.ink, 0.06)}, 0 4px 12px ${rgba(palette.ink, 0.08)}`,
        md: `0 4px 8px ${rgba(palette.ink, 0.08)}, 0 8px 20px ${rgba(palette.ink, 0.10)}`,
        lg: `0 8px 16px ${rgba(palette.ink, 0.10)}, 0 16px 40px ${rgba(palette.ink, 0.12)}`,
        xl: `0 16px 32px ${rgba(palette.ink, 0.12)}, 0 24px 64px ${rgba(palette.ink, 0.15)}`,
    };

    return {
        background: `linear-gradient(155deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.50) 42%, ${rgba(accent, intensity)} 100%)`,
        backdropFilter: 'blur(24px) saturate(200%)',
        WebkitBackdropFilter: 'blur(24px) saturate(200%)',
        border: `1px solid rgba(255,255,255,0.6)`,
        boxShadow: [
            `inset 0 1px 0 rgba(255,255,255,0.95)`,
            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
            `inset 0 0 0 1px ${rgba(accent, 0.04)}`,
            elevations[elevation] || elevations.md,
            `0 12px 40px -8px ${rgba(accent, 0.15)}`,
        ].join(', '),
        overflow: 'hidden',
        transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1), border-color .5s ease',
    };
};

const sheenLayer = (accent) => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .5s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.7), ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
    content: '""',
    borderRadius: 'inherit',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    };
    const onPointerLeave = () => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    };
    return { ref, onPointerMove, onPointerLeave };
};

// ─────────────────────────────────────────────────────────────────────────────
// 3 · GLASS COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const GlassCard = ({ children, accent = palette.rust, sx = {}, delay = 0, hover = true, elevation = 'md', ...props }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={hover ? { y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } } : {}}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(accent, 0.12, elevation),
                    position: 'relative',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '20px',
                    border: `1.5px solid ${rgba(accent, 0.10)}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '&::after': sheenLayer(accent),
                    transform: isHovered && hover ? 'translateY(-4px) scale(1.003)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered && hover 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(accent, 0.08)}`,
                            `0 20px 60px -16px ${rgba(accent, 0.30)}`,
                            `0 8px 24px ${rgba(palette.ink, 0.10)}`,
                        ].join(', ')
                        : liquidGlass(accent, 0.12, elevation).boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(accent, 0.4)}, transparent)`,
                        opacity: isHovered && hover ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '20px 20px 0 0',
                        zIndex: 2,
                    },
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Paper>
        </motion.div>
    );
};

const GlassMenuItem = ({ children, onClick, isActive, color = palette.rust, delay = 0, ...props }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: '100%' }}
        >
            <MenuItem
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                onClick={onClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    position: 'relative',
                    borderRadius: '12px',
                    marginX: '6px',
                    marginBottom: '2px',
                    padding: '10px 14px',
                    background: isActive ? rgba(color, 0.08) : 'transparent',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    transform: isHovered ? 'translateX(6px)' : 'translateX(0)',
                    backdropFilter: isHovered ? 'blur(4px)' : 'none',
                    '&:hover': {
                        background: rgba(color, 0.10),
                        transform: 'translateX(6px)',
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '10%',
                        height: '80%',
                        width: '3px',
                        borderRadius: '0 3px 3px 0',
                        background: isActive ? color : 'transparent',
                        transition: 'background 0.3s ease',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity .4s ease',
                        background: `radial-gradient(300px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.5), ${rgba(color, 0.06)} 40%, transparent 70%)`,
                        mixBlendMode: 'soft-light',
                        borderRadius: '12px',
                    },
                    ...props.sx,
                }}
            >
                {children}
            </MenuItem>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 4 · FLOATING ORBS
// ─────────────────────────────────────────────────────────────────────────────

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 400, top: '-10%', left: '2%', duration: 28, delay: 0 },
        { color: palette.pine, size: 340, top: '15%', left: '85%', duration: 34, delay: 2 },
        { color: palette.gold, size: 280, top: '55%', left: '-8%', duration: 26, delay: 1 },
        { color: palette.purple, size: 320, top: '75%', left: '72%', duration: 32, delay: 3 },
        { color: palette.teal, size: 240, top: '35%', left: '45%', duration: 30, delay: 1.5 },
    ];

    return (
        <Box aria-hidden="true" sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.25, 0.5, 0.25], x: [0, 40, -30, 0], y: [0, -35, 25, 0], scale: [1, 1.1, 0.95, 1] }}
                    transition={{ duration: orb.duration, delay: orb.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${orb.color}20, ${orb.color}00 70%)`,
                        filter: 'blur(8px)',
                    }}
                />
            ))}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5 · SECTION RULE
// ─────────────────────────────────────────────────────────────────────────────

const SectionRule = ({ color = palette.rust }) => (
    <Box sx={{ width: 40, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${color}, transparent)`, mt: 0.75 }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// 6 · TAB PANEL
// ─────────────────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index, ...props }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}
            {...props}
        >
            {value === index && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                >
                    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>{children}</Box>
                </motion.div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7 · CHART CARD
// ─────────────────────────────────────────────────────────────────────────────

const ChartCard = ({ title, children, height = 350, action, color = palette.rust, fullWidth = false }) => (
    <GlassCard
        accent={color}
        hover={false}
        elevation="md"
        sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
    >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.ink, fontFamily: fontDisplay }}>
                {title}
            </Typography>
            {action}
        </Box>
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, height, minHeight: height, overflow: 'hidden' }}>
            {children}
        </Box>
    </GlassCard>
);

// ─────────────────────────────────────────────────────────────────────────────
// 8 · STAT CARD
// ─────────────────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color, gradient, delay, subtitle, change, trend }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);
    const [isHovered, setIsHovered] = useState(false);
    const { ref, onPointerMove, onPointerLeave } = useSheen();

    useEffect(() => {
        if (typeof value === 'number' && value > 0) {
            const node = nodeRef.current;
            if (!node || hasRun.current) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !hasRun.current) {
                        hasRun.current = true;
                        let current = 0;
                        const step = Math.ceil(value / 40);
                        const interval = setInterval(() => {
                            current += step;
                            if (current >= value) {
                                current = value;
                                clearInterval(interval);
                            }
                            if (node) node.textContent = Math.round(current).toLocaleString();
                        }, 25);
                        return () => clearInterval(interval);
                    }
                },
                { threshold: 0.3 }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }
    }, [value]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: '100%', minWidth: 0, maxWidth: '100%' }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquidGlass(color, 0.15, 'lg'),
                    position: 'relative',
                    borderRadius: '24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '&::after': sheenLayer(color),
                    transform: isHovered ? 'translateY(-6px) scale(1.005)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered 
                        ? [
                            `inset 0 1px 0 rgba(255,255,255,0.95)`,
                            `inset 0 -1px 0 rgba(255,255,255,0.30)`,
                            `inset 0 0 0 1px ${rgba(color, 0.10)}`,
                            `0 24px 64px -16px ${rgba(color, 0.35)}`,
                            `0 12px 32px ${rgba(palette.ink, 0.12)}`,
                        ].join(', ')
                        : liquidGlass(color, 0.15, 'lg').boxShadow,
                    transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, transparent, ${rgba(color, 0.5)}, transparent)`,
                        opacity: isHovered ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        borderRadius: '24px 24px 0 0',
                        zIndex: 2,
                    },
                }}
            >
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <motion.div
                            whileHover={{ scale: 1.12, rotate: -5, transition: { type: 'spring', stiffness: 400, damping: 17 } }}
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: gradient || `linear-gradient(135deg, ${color}, ${color}80)`,
                                color: '#fff',
                                boxShadow: `0 4px 20px ${rgba(color, 0.35)}`,
                                position: 'relative',
                                border: `2px solid ${rgba(color, 0.15)}`,
                                willChange: 'transform',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
                        </motion.div>
                        {change && (
                            <Chip
                                label={change}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 26,
                                    px: 0.75,
                                    bgcolor: trend === 'up' ? rgba(palette.pine, 0.12) : rgba(palette.danger, 0.12),
                                    color: trend === 'up' ? palette.pine : palette.danger,
                                    border: `1.5px solid ${trend === 'up' ? rgba(palette.pine, 0.2) : rgba(palette.danger, 0.2)}`,
                                    backdropFilter: 'blur(4px)',
                                }}
                                icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                            />
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: fontDisplay,
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            lineHeight: 1,
                            background: `linear-gradient(135deg, ${palette.ink} 30%, ${color})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'inline-block',
                        }}
                    >
                        <span ref={nodeRef}>0</span>
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem' }}>
                        {title}
                    </Typography>
                    {subtitle && <Typography sx={{ color: palette.inkLight, fontSize: '0.7rem', mt: 0.25 }}>{subtitle}</Typography>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 2 }}>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.success, position: 'relative', zIndex: 1 }} />
                            <motion.div
                                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.1, 0.3] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                style={{ position: 'absolute', width: 16, height: 16, borderRadius: '50%', background: palette.success, opacity: 0.3 }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ color: palette.success, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
                            Live Data
                        </Typography>
                    </Box>
                </CardContent>
            </Paper>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 9 · MAIN ANALYTICS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const Analytics = () => {
    const { user, userData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [timeRange, setTimeRange] = useState('month');
    const [exportFormat, setExportFormat] = useState('pdf');
    const [anchorEl, setAnchorEl] = useState(null);
    const [openReportDialog, setOpenReportDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // ─── Data States ────────────────────────────────────────────────────────
    const [summary, setSummary] = useState({
        totalUsers: 0,
        totalCompanies: 0,
        totalMeetings: 0,
        totalRevenue: 0,
        activeUsers: 0,
        completedMeetings: 0,
        cancelledMeetings: 0,
        scheduledMeetings: 0,
        completionRate: 0,
    });

    const [meetingData, setMeetingData] = useState([]);
    const [userDataState, setUserDataState] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [companyData, setCompanyData] = useState([]);
    const [detailedMeetings, setDetailedMeetings] = useState([]);
    const [radarData, setRadarData] = useState([]);
    const [reports, setReports] = useState([]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ─── Canvas Styles ──────────────────────────────────────────────────────
    const canvasSx = {
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        bgcolor: palette.canvas,
        fontFamily: fontBody,
        '& *': {
            boxSizing: 'border-box',
        },
        '& .MuiPaper-root, & .MuiCard-root': {
            minWidth: 0,
            maxWidth: '100%',
        },
        '& .recharts-responsive-container': {
            minWidth: '0 !important',
            maxWidth: '100% !important',
        },
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.025%27/%3E%3C/svg%3E")',
            animation: 'grainDrift 9s steps(6) infinite',
        },
    };

    // ─────────────────────────────────────────────────────────────────────────
    // GENERATE REPORT FUNCTION
    // ─────────────────────────────────────────────────────────────────────────

    const generateReport = (type) => {
        const reportNames = {
            full: 'Complete System Report',
            meeting: 'Meeting Analytics Report',
            user: 'User Engagement Report',
            revenue: 'Revenue Report',
            performance: 'Performance Report',
        };

        const reportDescriptions = {
            full: 'Comprehensive system analytics with all metrics',
            meeting: 'Detailed meeting statistics and trends analysis',
            user: 'User growth, engagement, and verification metrics',
            revenue: 'Revenue trends and financial insights',
            performance: 'Platform performance and completion metrics',
        };

        const newReport = {
            id: Date.now(),
            title: reportNames[type] || reportNames.full,
            type: type || 'full',
            description: reportDescriptions[type] || reportDescriptions.full,
            date: new Date().toISOString().split('T')[0],
            status: 'completed',
            size: `${(Math.random() * 3 + 1).toFixed(1)} MB`,
            format: 'pdf',
        };

        setReports(prev => [newReport, ...prev]);
        toast.success(`${newReport.title} generated successfully!`);
        setOpenReportDialog(false);

        const title = `${newReport.title.replace(/\s/g, '-')}-${new Date().toISOString().split('T')[0]}`;
        
        if (type === 'full' || type === 'performance' || type === 'meeting') {
            exportToPDF(detailedMeetings, title);
        } else if (type === 'user' || type === 'revenue') {
            exportToExcel(detailedMeetings, title);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // EXPORT FUNCTIONS
    // ─────────────────────────────────────────────────────────────────────────

    const exportToPDF = (data, title) => {
        try {
            const doc = new jsPDF('landscape', 'pt', 'a4');
            
            doc.setFontSize(18);
            doc.setTextColor(26, 21, 18);
            doc.text(title || 'Analytics Report', 40, 40);
            
            doc.setFontSize(10);
            doc.setTextColor(92, 82, 72);
            doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 60);

            const summaryData = [
                ['Metric', 'Value'],
                ['Total Users', summary.totalUsers.toString()],
                ['Active Users', summary.activeUsers.toString()],
                ['Total Companies', summary.totalCompanies.toString()],
                ['Total Meetings', summary.totalMeetings.toString()],
                ['Completed Meetings', summary.completedMeetings.toString()],
                ['Total Revenue', `$${summary.totalRevenue.toLocaleString()}`],
                ['Completion Rate', `${summary.completionRate.toFixed(1)}%`],
            ];

            doc.autoTable({
                head: [summaryData[0]],
                body: summaryData.slice(1),
                startY: 80,
                styles: { fontSize: 9, cellPadding: 4 },
                headStyles: { fillColor: [199, 91, 46] },
                theme: 'striped',
            });

            if (data && data.length > 0) {
                const meetingTableData = data.slice(0, 20).map(m => [
                    m.title || 'Untitled',
                    m.date || 'N/A',
                    m.status || 'unknown',
                    `$${m.revenue || 0}`,
                    `${m.duration || 0}min`,
                    `${m.attendees || 0}`,
                ]);

                doc.autoTable({
                    head: [['Title', 'Date', 'Status', 'Revenue', 'Duration', 'Attendees']],
                    body: meetingTableData,
                    startY: doc.autoTable.previous.finalY + 20,
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: [45, 90, 71] },
                    theme: 'striped',
                });
            }

            doc.save(`${title || 'analytics-report'}-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('PDF exported successfully!');
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error('Failed to export PDF');
        }
    };

    const exportToExcel = (data, title) => {
        try {
            const workbook = XLSX.utils.book_new();

            const summarySheetData = [
                ['Metric', 'Value'],
                ['Total Users', summary.totalUsers],
                ['Active Users', summary.activeUsers],
                ['Total Companies', summary.totalCompanies],
                ['Total Meetings', summary.totalMeetings],
                ['Completed Meetings', summary.completedMeetings],
                ['Cancelled Meetings', summary.cancelledMeetings],
                ['Scheduled Meetings', summary.scheduledMeetings],
                ['Total Revenue', summary.totalRevenue],
                ['Completion Rate', `${summary.completionRate.toFixed(1)}%`],
            ];
            const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

            if (detailedMeetings.length > 0) {
                const meetingsSheetData = [
                    ['Title', 'Date', 'Status', 'Revenue', 'Duration', 'Attendees', 'Type'],
                    ...detailedMeetings.map(m => [
                        m.title || 'Untitled',
                        m.date || 'N/A',
                        m.status || 'unknown',
                        m.revenue || 0,
                        m.duration || 0,
                        m.attendees || 0,
                        m.type || 'N/A',
                    ])
                ];
                const meetingsSheet = XLSX.utils.aoa_to_sheet(meetingsSheetData);
                XLSX.utils.book_append_sheet(workbook, meetingsSheet, 'Meetings');
            }

            const fileName = `${title || 'analytics-report'}-${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            toast.success('Excel exported successfully!');
        } catch (error) {
            console.error('Excel export error:', error);
            toast.error('Failed to export Excel');
        }
    };

    const handleExport = () => {
        const title = `Analytics-Report-${new Date().toISOString().split('T')[0]}`;
        
        if (exportFormat === 'pdf') {
            exportToPDF(detailedMeetings, title);
        } else {
            exportToExcel(detailedMeetings, title);
        }
        setAnchorEl(null);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DATA FETCHING FUNCTIONS
    // ─────────────────────────────────────────────────────────────────────────

    const fetchAnalyticsData = async () => {
        setLoading(true);
        try {
            const meetingsQuery = query(collection(db, 'meetings'), orderBy('createdAt', 'desc'));
            const meetingsSnapshot = await getDocs(meetingsQuery);
            const meetings = [];
            meetingsSnapshot.forEach((doc) => {
                const data = doc.data();
                meetings.push({ 
                    id: doc.id, 
                    ...data,
                    scheduledAt: data.scheduledAt?.toDate?.() || data.scheduledAt || null,
                    createdAt: data.createdAt?.toDate?.() || data.createdAt || null,
                });
            });

            const usersSnapshot = await getDocs(collection(db, 'users'));
            const users = [];
            usersSnapshot.forEach((doc) => {
                const data = doc.data();
                users.push({ 
                    id: doc.id, 
                    ...data,
                    createdAt: data.createdAt?.toDate?.() || data.createdAt || null,
                });
            });

            const companiesSnapshot = await getDocs(collection(db, 'companies'));
            const companies = [];
            companiesSnapshot.forEach((doc) => {
                const data = doc.data();
                companies.push({ 
                    id: doc.id, 
                    ...data,
                    createdAt: data.createdAt?.toDate?.() || data.createdAt || null,
                });
            });

            processAnalyticsData(meetings, users, companies);

        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    const processAnalyticsData = (meetings, users, companies) => {
        const totalUsers = users.length;
        const totalCompanies = companies.length;
        const totalMeetings = meetings.length;
        const activeUsers = users.filter(u => u.isActive === true).length;
        
        const completedMeetings = meetings.filter(m => m.status === 'completed' || m.status === 'done').length;
        const cancelledMeetings = meetings.filter(m => m.status === 'cancelled').length;
        const scheduledMeetings = meetings.filter(m => m.status === 'scheduled' || m.status === 'confirmed' || m.status === 'pending').length;
        
        const completionRate = totalMeetings > 0 ? (completedMeetings / totalMeetings) * 100 : 0;
        
        let totalRevenue = 0;
        meetings.forEach(m => {
            if (m.revenue) totalRevenue += parseFloat(m.revenue) || 0;
        });

        setSummary({
            totalUsers,
            totalCompanies,
            totalMeetings,
            totalRevenue,
            activeUsers,
            completedMeetings,
            cancelledMeetings,
            scheduledMeetings,
            completionRate,
        });

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const meetingMonthly = months.map((month, index) => {
            const monthMeetings = meetings.filter(m => {
                if (m.scheduledAt) {
                    const date = m.scheduledAt instanceof Date ? m.scheduledAt : new Date(m.scheduledAt);
                    return date.getMonth() === index && !isNaN(date.getMonth());
                }
                return false;
            });
            
            return {
                month,
                meetings: monthMeetings.length,
                completed: monthMeetings.filter(m => m.status === 'completed' || m.status === 'done').length,
                cancelled: monthMeetings.filter(m => m.status === 'cancelled').length,
                scheduled: monthMeetings.filter(m => m.status === 'scheduled' || m.status === 'confirmed').length,
            };
        });
        setMeetingData(meetingMonthly);

        const userMonthly = months.map((month, index) => {
            const monthUsers = users.filter(u => {
                if (u.createdAt) {
                    const date = u.createdAt instanceof Date ? u.createdAt : new Date(u.createdAt);
                    return date.getMonth() === index && !isNaN(date.getMonth());
                }
                return false;
            });
            return {
                month,
                users: monthUsers.length,
                active: monthUsers.filter(u => u.isActive === true).length,
                verified: monthUsers.filter(u => u.isVerified === true).length,
            };
        });
        setUserDataState(userMonthly);

        const revenueMonthly = months.map((month, index) => {
            let revenue = 0;
            meetings.forEach(m => {
                if (m.scheduledAt) {
                    const date = m.scheduledAt instanceof Date ? m.scheduledAt : new Date(m.scheduledAt);
                    if (date.getMonth() === index && !isNaN(date.getMonth())) {
                        revenue += parseFloat(m.revenue) || 0;
                    }
                }
            });
            return {
                month,
                revenue: Math.round(revenue * 100) / 100,
            };
        });
        setRevenueData(revenueMonthly);

        const companyTypes = ['operator', 'vendor', 'aggregator', 'solution_provider', 'consultant', 'mvno', 'reseller'];
        const colors = [palette.rust, palette.purple, palette.teal, palette.gold, palette.pine, palette.info, palette.admin];
        const companyDistribution = companyTypes.map((type, index) => ({
            name: type.replace('_', ' ').toUpperCase(),
            value: companies.filter(c => c.companyType === type).length,
            color: colors[index % colors.length],
        }));
        setCompanyData(companyDistribution);

        const detailed = meetings.slice(0, 100).map((m) => ({
            id: m.id,
            title: m.title || 'Untitled Meeting',
            date: m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString() : 'N/A',
            status: m.status || 'unknown',
            revenue: parseFloat(m.revenue) || 0,
            duration: m.duration || 0,
            attendees: m.attendees?.length || 0,
            type: m.type || 'general',
        }));
        setDetailedMeetings(detailed);

        const radar = [
            { subject: 'Meetings', A: Math.min((totalMeetings / 50) * 100, 100), fullMark: 100 },
            { subject: 'Users', A: Math.min((totalUsers / 100) * 100, 100), fullMark: 100 },
            { subject: 'Revenue', A: Math.min((totalRevenue / 10000) * 100, 100), fullMark: 100 },
            { subject: 'Completion', A: Math.min(completionRate, 100), fullMark: 100 },
            { subject: 'Growth', A: Math.min(users.length > 0 ? (activeUsers / totalUsers) * 100 : 0, 100), fullMark: 100 },
            { subject: 'Engagement', A: Math.min(meetings.length > 0 ? (completedMeetings / totalMeetings) * 100 : 0, 100), fullMark: 100 },
        ];
        setRadarData(radar);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // STATS DATA
    // ─────────────────────────────────────────────────────────────────────────

    const statsData = [
        {
            title: 'Total Users',
            value: summary.totalUsers,
            icon: <PeopleIcon  key="config-1050-0-2" />,
            color: palette.rust,
            gradient: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
            subtitle: `${summary.activeUsers} active`,
            change: summary.totalUsers > 0 ? `${Math.round((summary.activeUsers / summary.totalUsers) * 100)}%` : '0%',
            trend: summary.activeUsers > summary.totalUsers / 2 ? 'up' : 'down',
        },
        {
            title: 'Companies',
            value: summary.totalCompanies,
            icon: <BusinessIcon  key="config-1060-1-2" />,
            color: palette.purple,
            gradient: `linear-gradient(135deg, ${palette.purple}, ${palette.purpleLight})`,
            subtitle: 'Registered',
            change: summary.totalCompanies > 0 ? '+active' : '0',
            trend: 'up',
        },
        {
            title: 'Meetings',
            value: summary.totalMeetings,
            icon: <EventIcon  key="config-1070-2-2" />,
            color: palette.teal,
            gradient: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
            subtitle: `${summary.scheduledMeetings} scheduled`,
            change: summary.totalMeetings > 0 ? `${Math.round((summary.completedMeetings / summary.totalMeetings) * 100)}% done` : '0%',
            trend: summary.completionRate > 50 ? 'up' : 'down',
        },
        {
            title: 'Revenue',
            value: summary.totalRevenue > 0 ? formatCurrency(summary.totalRevenue) : '$0',
            icon: <MoneyIcon  key="config-1080-3-2" />,
            color: palette.gold,
            gradient: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
            subtitle: 'Total generated',
            change: summary.totalRevenue > 0 ? '+active' : '0',
            trend: 'up',
        },
        {
            title: 'Completion Rate',
            value: `${summary.completionRate.toFixed(1)}%`,
            icon: <CheckCircleIcon  key="config-1090-4-2" />,
            color: palette.pine,
            gradient: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
            subtitle: 'Meeting completion',
            change: summary.completionRate > 50 ? `${Math.round(summary.completionRate / 10)}%` : '0%',
            trend: summary.completionRate > 50 ? 'up' : 'down',
        },
        {
            title: 'Active Users',
            value: summary.activeUsers,
            icon: <RocketIcon  key="config-1100-5-2" />,
            color: palette.info,
            gradient: `linear-gradient(135deg, ${palette.info}, ${palette.navy})`,
            subtitle: 'Currently active',
            change: summary.activeUsers > 0 ? `${Math.round((summary.activeUsers / summary.totalUsers) * 100)}%` : '0%',
            trend: summary.activeUsers > summary.totalUsers / 3 ? 'up' : 'down',
        },
    ];

    const tabs = [
        { label: 'Overview', icon: <DashboardIcon  key="config-1113-0-1" />, color: palette.rust },
        { label: 'Meetings', icon: <EventIcon  key="config-1114-1-1" />, color: palette.purple },
        { label: 'Users', icon: <PeopleIcon  key="config-1115-2-1" />, color: palette.teal },
        { label: 'Revenue', icon: <MoneyIcon  key="config-1116-3-1" />, color: palette.gold },
        { label: 'Reports', icon: <AssessmentIcon  key="config-1117-4-1" />, color: palette.pine },
    ];

    const COLORS = [palette.rust, palette.purple, palette.teal, palette.gold, palette.pine, palette.info, palette.admin];

    // ─────────────────────────────────────────────────────────────────────────
    // USE EFFECT
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchAnalyticsData();

        const unsubscribeMeetings = onSnapshot(
            query(collection(db, 'meetings'), orderBy('createdAt', 'desc'), limit(500)),
            () => {
                fetchAnalyticsData();
            },
            (error) => {
                console.warn('Meetings listener error:', error);
            }
        );

        const unsubscribeUsers = onSnapshot(
            collection(db, 'users'),
            () => {
                fetchAnalyticsData();
            },
            (error) => {
                console.warn('Users listener error:', error);
            }
        );

        const unsubscribeCompanies = onSnapshot(
            collection(db, 'companies'),
            () => {
                fetchAnalyticsData();
            },
            (error) => {
                console.warn('Companies listener error:', error);
            }
        );

        return () => {
            unsubscribeMeetings();
            unsubscribeUsers();
            unsubscribeCompanies();
        };
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Box sx={canvasSx}>
            <style>{FONT_IMPORT}</style>
            <FloatingOrbs />
            

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    py: { xs: 2, sm: 2.5, md: 3 },
                    px: { xs: 1.5, sm: 2.5, md: 3 },
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    mx: 0,
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: loading ? 'center' : 'flex-start',
                    minHeight: 'calc(100vh - 72px)',
                }}
            >
                
                {loading ? (
                    // ─── CENTERED LOADER ──────────────────────────────────────
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flex: 1,
                        gap: 3,
                    }}>
                        <motion.div
                            animate={{ 
                                rotate: 360,
                                scale: [1, 1.1, 1],
                            }}
                            transition={{ 
                                rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
                                scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                            }}
                        >
                            <CircularProgress 
                                size={80} 
                                sx={{ 
                                    color: palette.rust,
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }} 
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Typography 
                                sx={{ 
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: palette.ink,
                                    letterSpacing: '0.02em',
                                }}
                            >
                                Loading Analytics...
                            </Typography>
                            <Typography 
                                sx={{ 
                                    fontFamily: fontBody,
                                    fontSize: '0.85rem',
                                    color: palette.inkLight,
                                    textAlign: 'center',
                                    mt: 0.5,
                                }}
                            >
                                Fetching real-time data from Firebase
                            </Typography>
                        </motion.div>
                    </Box>
                ) : (
                    // ─── MAIN CONTENT ──────────────────────────────────────────
                    <>
                        {/* Header Section */}
                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, mb: { xs: 2.5, md: 3 } }}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-end' }, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: fontDisplay,
                                                fontWeight: 600,
                                                fontSize: { xs: '1.6rem', md: '2rem' },
                                                color: palette.ink,
                                                letterSpacing: '-0.01em',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Analytics Dashboard
                                        </Typography>
                                        <Typography sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.95rem' }}>
                                            Real-time insights and performance metrics from Firebase
                                        </Typography>
                                        <SectionRule />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, '& > *': { minWidth: 0 } }}>
                                        <Chip
                                            icon={<LiveIcon sx={{ fontSize: 11, color: `${palette.pine} !important` }} />}
                                            label="Live"
                                            sx={{
                                                bgcolor: 'rgba(45,90,71,0.08)',
                                                color: palette.pine,
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 26,
                                                borderRadius: '16px',
                                                border: '1px solid rgba(45,90,71,0.12)',
                                                backdropFilter: 'blur(4px)',
                                            }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                                            <Select 
                                                value={timeRange} 
                                                onChange={(e) => setTimeRange(e.target.value)}
                                                sx={{
                                                    borderRadius: '12px',
                                                    backgroundColor: palette.paper,
                                                    backdropFilter: 'blur(8px)',
                                                    border: `1px solid ${palette.hairline}`,
                                                    '&:hover': {
                                                        borderColor: palette.rust,
                                                        backgroundColor: rgba(palette.rust, 0.04),
                                                        boxShadow: `0 4px 12px ${palette.rust}15`,
                                                    },
                                                    '&.Mui-focused': {
                                                        borderColor: palette.rust,
                                                        backgroundColor: rgba(palette.rust, 0.04),
                                                        boxShadow: `0 4px 16px ${palette.rust}20`,
                                                    }
                                                }}
                                            >
                                                <MenuItem value="week">Last 7 Days</MenuItem>
                                                <MenuItem value="month">Last 30 Days</MenuItem>
                                                <MenuItem value="quarter">Last 90 Days</MenuItem>
                                                <MenuItem value="year">Last 12 Months</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RefreshIcon />}
                                                onClick={fetchAnalyticsData}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    borderColor: palette.hairline,
                                                    color: palette.ink,
                                                    backdropFilter: 'blur(4px)',
                                                    '&:hover': { borderColor: palette.rust, bgcolor: `${palette.rust}08` }
                                                }}
                                            >
                                                Refresh
                                            </Button>
                                        </motion.div>
                                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                            <Button 
                                                variant="contained" 
                                                disableElevation
                                                startIcon={<DownloadIcon />} 
                                                onClick={handleMenuOpen}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                    boxShadow: '0 8px 24px -8px rgba(199,91,46,0.45)',
                                                    '&:hover': {
                                                        boxShadow: '0 12px 32px -8px rgba(199,91,46,0.55)',
                                                        transform: 'translateY(-2px)',
                                                    }
                                                }}
                                            >
                                                Export
                                            </Button>
                                        </motion.div>
                                        <Menu 
                                            anchorEl={anchorEl} 
                                            open={Boolean(anchorEl)} 
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        ...liquidGlass(palette.rust, 0.08, 'lg'),
                                                        borderRadius: '20px',
                                                        minWidth: 220,
                                                        p: 1,
                                                        '&::after': sheenLayer(palette.rust),
                                                        border: `1px solid rgba(255,255,255,0.6)`,
                                                        boxShadow: `0 20px 60px -12px ${rgba(palette.rust, 0.3)}, 0 8px 24px ${rgba(palette.ink, 0.08)}`,
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuList sx={{ py: 0.5 }}>
                                                <GlassMenuItem onClick={() => { setExportFormat('pdf'); handleExport(); }} color={palette.rust} delay={0}>
                                                    <ListItemIcon><PdfIcon fontSize="small" sx={{ color: palette.rust }} /></ListItemIcon>
                                                    <ListItemText primary="Export as PDF" />
                                                </GlassMenuItem>
                                                <GlassMenuItem onClick={() => { setExportFormat('excel'); handleExport(); }} color={palette.pine} delay={0.05}>
                                                    <ListItemIcon><TableChartIcon fontSize="small" sx={{ color: palette.pine }} /></ListItemIcon>
                                                    <ListItemText primary="Export as Excel" />
                                                </GlassMenuItem>
                                                <GlassMenuItem onClick={() => { setExportFormat('csv'); handleExport(); }} color={palette.teal} delay={0.1}>
                                                    <ListItemIcon><DescriptionIcon fontSize="small" sx={{ color: palette.teal }} /></ListItemIcon>
                                                    <ListItemText primary="Export as CSV" />
                                                </GlassMenuItem>
                                                <Divider sx={{ my: 1, borderColor: palette.hairline }} />
                                                <GlassMenuItem onClick={() => setOpenReportDialog(true)} color={palette.rust} delay={0.15}>
                                                    <ListItemIcon><AddIcon fontSize="small" sx={{ color: palette.rust }} /></ListItemIcon>
                                                    <ListItemText primary="Generate Report" primaryTypographyProps={{ fontWeight: 700 }} />
                                                </GlassMenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Box>
                                </Box>
                                <Divider sx={{ mt: 3, borderColor: palette.hairline }} />
                            </motion.div>
                        </Box>

                        {/* Stats Cards */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' },
                                gap: { xs: 1.5, sm: 2 },
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                mb: { xs: 2.5, md: 3 },
                            }}
                        >
                            {statsData.map((stat, index) => (
                                <StatCard
                                    key={stat.title}
                                    title={stat.title}
                                    value={stat.value}
                                    icon={stat.icon}
                                    color={stat.color}
                                    gradient={stat.gradient}
                                    delay={index * 0.05}
                                    subtitle={stat.subtitle}
                                    change={stat.change}
                                    trend={stat.trend}
                                />
                            ))}
                        </Box>

                        {/* Main Glass Card with Tabs */}
                        <GlassCard accent={palette.rust} delay={0.1} hover={false} elevation="lg" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: 0, overflow: 'hidden' }}>
                            <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, borderBottom: `1px solid ${palette.hairline}`, px: { xs: 0.5, sm: 1.5 }, pt: 0.5, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin' }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        minHeight: 56,
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            py: 1.5,
                                            minHeight: 48,
                                            minWidth: 'auto',
                                            px: { xs: 1.5, sm: 2.5 },
                                            borderRadius: '10px 10px 0 0',
                                            transition: 'all 0.3s ease',
                                            color: palette.inkLight,
                                            fontFamily: fontBody,
                                            '&:hover': {
                                                color: palette.ink,
                                                bgcolor: 'rgba(26,21,18,0.02)',
                                            },
                                            '&.Mui-selected': {
                                                color: palette.ink,
                                            }
                                        },
                                        '& .MuiTabs-indicator': {
                                            height: 3,
                                            borderRadius: '3px',
                                            backgroundColor: tabs[tabValue]?.color || palette.rust,
                                        }
                                    }}
                                >
                                    {tabs.map((tab, index) => (
                                        <Tab
                                            key={index}
                                            icon={React.cloneElement(tab.icon, {
                                                sx: { fontSize: { xs: 16, sm: 18 }, color: tabValue === index ? tab.color : palette.inkLight }
                                            })}
                                            label={tab.label}
                                            iconPosition="start"
                                        />
                                    ))}
                                </Tabs>
                            </Box>

                            <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
                                {/* Overview Tab */}
                                <TabPanel value={tabValue} index={0}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                        <Box sx={{ flex: '1 1 100%', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="📊 Meeting Trends Overview" height={235} color={palette.rust} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={meetingData}>
                                                        <defs>
                                                            <linearGradient id="overviewScheduled" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.rust} stopOpacity={0.4}/>
                                                                <stop offset="95%" stopColor={palette.rust} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                            <linearGradient id="overviewCompleted" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.pine} stopOpacity={0.4}/>
                                                                <stop offset="95%" stopColor={palette.pine} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                            <linearGradient id="overviewCancelled" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.danger} stopOpacity={0.3}/>
                                                                <stop offset="95%" stopColor={palette.danger} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} strokeOpacity={0.5} />
                                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                                                        <Bar yAxisId="left" dataKey="meetings" fill="url(#overviewScheduled)" radius={[6, 6, 0, 0]} barSize={30} />
                                                        <Area yAxisId="right" type="monotone" dataKey="completed" stroke={palette.pine} strokeWidth={2} fill="url(#overviewCompleted)" />
                                                        <Area yAxisId="right" type="monotone" dataKey="cancelled" stroke={palette.danger} strokeWidth={2} fill="url(#overviewCancelled)" />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                            <ChartCard title="🏢 Company Distribution" height={300} color={palette.purple}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={companyData.filter(c => c.value > 0)}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={50}
                                                            outerRadius={85}
                                                            paddingAngle={3}
                                                            dataKey="value"
                                                            label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                                                            labelLine={{ stroke: palette.hairline, strokeWidth: 1 }}
                                                        >
                                                            {companyData.filter(c => c.value > 0).map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} stroke={palette.paper} strokeWidth={2} />
                                                            ))}
                                                        </Pie>
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} layout="vertical" verticalAlign="middle" align="right" />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                            <ChartCard title="🎯 Performance Radar" height={300} color={palette.teal}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <RadarChart data={radarData}>
                                                        <PolarGrid stroke={palette.hairline} />
                                                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: palette.inkLight }} />
                                                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: palette.inkLight }} />
                                                        <Radar
                                                            name="Performance"
                                                            dataKey="A"
                                                            stroke={palette.rust}
                                                            strokeWidth={2}
                                                            fill={palette.rust}
                                                            fillOpacity={0.3}
                                                        />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                                                    </RadarChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ flex: '1 1 100%', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="📈 Revenue Trends" height={300} color={palette.gold} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={revenueData}>
                                                        <defs>
                                                            <linearGradient id="revenueBarOverview" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.gold} stopOpacity={0.8}/>
                                                                <stop offset="95%" stopColor={palette.gold} stopOpacity={0.2}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} strokeOpacity={0.5} />
                                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis tick={{ fontSize: 11, fill: palette.inkLight }} tickFormatter={(value) => formatCurrency(value)} />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                            formatter={(value) => typeof value === 'number' ? formatCurrency(value) : value}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                                        <Bar dataKey="revenue" fill="url(#revenueBarOverview)" radius={[6, 6, 0, 0]} barSize={30} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>
                                    </Box>
                                </TabPanel>

                                {/* Meetings Tab */}
                                <TabPanel value={tabValue} index={1}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="📅 Meeting Analytics" height={350} color={palette.purple} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={meetingData}>
                                                        <defs>
                                                            <linearGradient id="meetingBarGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.rust} stopOpacity={0.6}/>
                                                                <stop offset="95%" stopColor={palette.rust} stopOpacity={0.1}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} strokeOpacity={0.5} />
                                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                                        <Bar yAxisId="left" dataKey="meetings" fill="url(#meetingBarGrad)" radius={[6, 6, 0, 0]} barSize={28} />
                                                        <Line yAxisId="right" type="monotone" dataKey="completed" stroke={palette.pine} strokeWidth={3} dot={{ fill: palette.pine, r: 5, strokeWidth: 2, stroke: palette.paper }} />
                                                        <Line yAxisId="right" type="monotone" dataKey="cancelled" stroke={palette.danger} strokeWidth={2.5} strokeDasharray="4 4" dot={{ fill: palette.danger, r: 4, strokeWidth: 2, stroke: palette.paper }} />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.pine} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.rust, 0.12), color: palette.rust, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <EventIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Meeting Statistics
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                                        <Box>
                                                            <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Total Meetings</Typography>
                                                            <Typography variant="h5" fontWeight={700} sx={{ color: palette.ink }}>{summary.totalMeetings}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Completed</Typography>
                                                            <Typography variant="h5" fontWeight={700} sx={{ color: palette.pine }}>{summary.completedMeetings}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Cancelled</Typography>
                                                            <Typography variant="h5" fontWeight={700} sx={{ color: palette.danger }}>{summary.cancelledMeetings}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Scheduled</Typography>
                                                            <Typography variant="h5" fontWeight={700} sx={{ color: palette.info }}>{summary.scheduledMeetings}</Typography>
                                                        </Box>
                                                    </Box>
                                                </GlassCard>
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.purple} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.purple, 0.12), color: palette.purple, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <AssessmentIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Meeting Insights
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2.5}>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="body2" color={palette.inkLight}>Completion Rate</Typography>
                                                                <Typography variant="body2" fontWeight={600} color={palette.pine}>
                                                                    {summary.completionRate.toFixed(1)}%
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: 6, borderRadius: 4, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${Math.min(summary.completionRate, 100)}%` }}
                                                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                                                    style={{
                                                                        height: '100%',
                                                                        borderRadius: 4,
                                                                        background: `linear-gradient(90deg, ${palette.pine}, ${palette.pine}80)`,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Avg. Meetings / Month</Typography>
                                                            <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink }}>
                                                                {meetingData.length > 0 ? (meetingData.reduce((acc, m) => acc + m.meetings, 0) / 12).toFixed(1) : '0'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>
                                        </Box>

                                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <GlassCard accent={palette.teal} hover={false} elevation="md">
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                        Detailed Meeting Log
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            sx={{ width: { xs: '100%', sm: 260 }, minWidth: 0 }}
                                                            placeholder="Search meetings..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <SearchIcon sx={{ color: palette.inkLight, fontSize: 18 }} />
                                                                        </InputAdornment>
                                                                    ),
                                                                    sx: {
                                                                        borderRadius: '10px',
                                                                        bgcolor: palette.paper,
                                                                        backdropFilter: 'blur(8px)',
                                                                        borderColor: palette.hairline,
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <IconButton size="small" sx={{ color: palette.rust }} onClick={() => exportToExcel(detailedMeetings, 'meeting-log')}>
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                                                    <Table size="small" sx={{ minWidth: 760 }}>
                                                        <TableHead>
                                                            <TableRow sx={{ bgcolor: palette.canvasAlt }}>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }}>Title</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }}>Date</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }}>Status</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }} align="right">Revenue</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }} align="center">Duration</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }} align="center">Attendees</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: palette.ink }} align="center">Type</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {detailedMeetings
                                                                .filter(m => m.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                                                           m.status?.toLowerCase().includes(searchQuery.toLowerCase()))
                                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                                .map((meeting) => (
                                                                    <TableRow key={meeting.id} hover>
                                                                        <TableCell sx={{ fontWeight: 500 }}>{meeting.title}</TableCell>
                                                                        <TableCell>{meeting.date}</TableCell>
                                                                        <TableCell>
                                                                            <Chip 
                                                                                label={meeting.status} 
                                                                                size="small"
                                                                                sx={{
                                                                                    bgcolor: meeting.status === 'completed' || meeting.status === 'done' ? rgba(palette.success, 0.12) :
                                                                                            meeting.status === 'cancelled' ? rgba(palette.danger, 0.12) :
                                                                                            rgba(palette.info, 0.12),
                                                                                    color: meeting.status === 'completed' || meeting.status === 'done' ? palette.success :
                                                                                           meeting.status === 'cancelled' ? palette.danger :
                                                                                           palette.info,
                                                                                    fontWeight: 600,
                                                                                    fontSize: '0.65rem',
                                                                                    height: 22,
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="right">{formatCurrency(meeting.revenue || 0)}</TableCell>
                                                                        <TableCell align="center">{meeting.duration || 0}m</TableCell>
                                                                        <TableCell align="center">{meeting.attendees || 0}</TableCell>
                                                                        <TableCell align="center">
                                                                            <Chip 
                                                                                label={meeting.type || 'N/A'} 
                                                                                size="small"
                                                                                sx={{
                                                                                    bgcolor: rgba(palette.purple, 0.08),
                                                                                    color: palette.purple,
                                                                                    fontWeight: 500,
                                                                                    fontSize: '0.6rem',
                                                                                    height: 20,
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                <TablePagination
                                                    rowsPerPageOptions={[5, 10, 25]}
                                                    component="div"
                                                    count={detailedMeetings.length}
                                                    rowsPerPage={rowsPerPage}
                                                    page={page}
                                                    onPageChange={(e, p) => setPage(p)}
                                                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                                    sx={{ 
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        borderTop: `1px solid ${palette.hairline}`, 
                                                        mt: 1, 
                                                        pt: 1,
                                                        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' }, gap: 0.5 },
                                                        '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
                                                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                            fontFamily: fontBody,
                                                            color: palette.inkLight
                                                        }
                                                    }}
                                                />
                                            </GlassCard>
                                        </Box>
                                    </Box>
                                </TabPanel>

                                {/* Users Tab */}
                                <TabPanel value={tabValue} index={2}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="👤 User Analytics" height={350} color={palette.teal} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={userDataState}>
                                                        <defs>
                                                            <linearGradient id="usersGradFull" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.rust} stopOpacity={0.4}/>
                                                                <stop offset="95%" stopColor={palette.rust} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                            <linearGradient id="activeGradFull" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.pine} stopOpacity={0.4}/>
                                                                <stop offset="95%" stopColor={palette.pine} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                            <linearGradient id="verifiedGradFull" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.purple} stopOpacity={0.3}/>
                                                                <stop offset="95%" stopColor={palette.purple} stopOpacity={0.02}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} strokeOpacity={0.5} />
                                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                                        <Area type="monotone" dataKey="users" stackId="1" stroke={palette.rust} strokeWidth={2} fill="url(#usersGradFull)" />
                                                        <Area type="monotone" dataKey="active" stackId="1" stroke={palette.pine} strokeWidth={2} fill="url(#activeGradFull)" />
                                                        <Area type="monotone" dataKey="verified" stackId="1" stroke={palette.purple} strokeWidth={2} fill="url(#verifiedGradFull)" />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 10px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.rust} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.rust, 0.12), color: palette.rust, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <PeopleIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            User Statistics
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Total Users</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.ink }}>{summary.totalUsers}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Active Users</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.pine }}>{summary.activeUsers}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Verified Users</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.purple }}>
                                                                {userDataState.reduce((acc, m) => acc + m.verified, 0)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 10px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.pine} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.pine, 0.12), color: palette.pine, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <TrendingUpIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Engagement Metrics
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2.5}>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="body2" color={palette.inkLight}>Active Rate</Typography>
                                                                <Typography variant="body2" fontWeight={600} color={palette.pine}>
                                                                    {summary.totalUsers > 0 ? `${((summary.activeUsers / summary.totalUsers) * 100).toFixed(1)}%` : '0%'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: 6, borderRadius: 4, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${summary.totalUsers > 0 ? ((summary.activeUsers / summary.totalUsers) * 100) : 0}%` }}
                                                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                                                    style={{
                                                                        height: '100%',
                                                                        borderRadius: 4,
                                                                        background: `linear-gradient(90deg, ${palette.pine}, ${palette.pine}80)`,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="body2" color={palette.inkLight}>Verification Rate</Typography>
                                                                <Typography variant="body2" fontWeight={600} color={palette.purple}>
                                                                    {summary.totalUsers > 0 ? `${((userDataState.reduce((acc, m) => acc + m.verified, 0) / summary.totalUsers) * 100).toFixed(1)}%` : '0%'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: 6, borderRadius: 4, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${summary.totalUsers > 0 ? ((userDataState.reduce((acc, m) => acc + m.verified, 0) / summary.totalUsers) * 100) : 0}%` }}
                                                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                                                    style={{
                                                                        height: '100%',
                                                                        borderRadius: 4,
                                                                        background: `linear-gradient(90deg, ${palette.purple}, ${palette.purple}80)`,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 10px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.gold} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.gold, 0.12), color: palette.gold, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <SpeedIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Growth Insights
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Total Users</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.ink }}>{summary.totalUsers}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Avg. New Users / Month</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.ink }}>
                                                                {userDataState.length > 0 ? (userDataState.reduce((acc, m) => acc + m.users, 0) / 12).toFixed(1) : '0'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>
                                        </Box>
                                    </Box>
                                </TabPanel>

                                {/* Revenue Tab */}
                                <TabPanel value={tabValue} index={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="💰 Revenue Trends" height={350} color={palette.gold} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={revenueData}>
                                                        <defs>
                                                            <linearGradient id="revenueBarFull" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={palette.gold} stopOpacity={0.8}/>
                                                                <stop offset="95%" stopColor={palette.gold} stopOpacity={0.15}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.hairline} strokeOpacity={0.5} />
                                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.inkLight }} />
                                                        <YAxis tick={{ fontSize: 11, fill: palette.inkLight }} tickFormatter={(value) => formatCurrency(value)} />
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                            formatter={(value) => typeof value === 'number' ? formatCurrency(value) : value}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                                        <Bar dataKey="revenue" fill="url(#revenueBarFull)" radius={[6, 6, 0, 0]} barSize={32} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.gold} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.gold, 0.12), color: palette.gold, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <MoneyIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Revenue Summary
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Total Revenue</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.gold }}>
                                                                {formatCurrency(summary.totalRevenue)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Avg. Revenue / Month</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem">
                                                                {formatCurrency(summary.totalRevenue / 12)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Total Meetings with Revenue</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.ink }}>
                                                                {detailedMeetings.filter(m => m.revenue > 0).length}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>

                                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                <GlassCard accent={palette.info} hover={false} elevation="md">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                        <Box sx={{ bgcolor: rgba(palette.info, 0.12), color: palette.info, p: 1, borderRadius: 2, display: 'flex' }}>
                                                            <AnalyticsIcon />
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>
                                                            Revenue Insights
                                                        </Typography>
                                                    </Box>
                                                    <Stack spacing={2}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${palette.hairline}` }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Best Performing Month</Typography>
                                                            <Chip 
                                                                label={revenueData.reduce((max, m) => m.revenue > (max?.revenue || 0) ? m : max, revenueData[0])?.month || 'N/A'} 
                                                                size="small" 
                                                                sx={{ bgcolor: rgba(palette.gold, 0.12), color: palette.gold, fontWeight: 600 }} 
                                                            />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Revenue per Meeting</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.gold }}>
                                                                {summary.totalMeetings > 0 ? formatCurrency(summary.totalRevenue / summary.totalMeetings) : '$0'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color={palette.inkLight}>Revenue per User</Typography>
                                                            <Typography variant="body2" fontWeight={700} fontSize="1.1rem" sx={{ color: palette.gold }}>
                                                                {summary.totalUsers > 0 ? formatCurrency(summary.totalRevenue / summary.totalUsers) : '$0'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </GlassCard>
                                            </Box>
                                        </Box>

                                        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            <ChartCard title="📊 Revenue Distribution by Company Type" height={280} color={palette.gold} fullWidth>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={companyData.filter(c => c.value > 0)}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={55}
                                                            outerRadius={85}
                                                            paddingAngle={3}
                                                            dataKey="value"
                                                            label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                                                            labelLine={{ stroke: palette.hairline, strokeWidth: 1 }}
                                                        >
                                                            {companyData.filter(c => c.value > 0).map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} stroke={palette.paper} strokeWidth={2} />
                                                            ))}
                                                        </Pie>
                                                        <ChartTooltip 
                                                            contentStyle={{ 
                                                                borderRadius: '12px', 
                                                                border: `1px solid ${palette.hairline}`,
                                                                boxShadow: '0 4px 16px rgba(22,18,16,0.04)',
                                                                padding: '12px 16px',
                                                                background: palette.paper,
                                                                backdropFilter: 'blur(8px)',
                                                            }}
                                                        />
                                                        <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Box>
                                    </Box>
                                </TabPanel>

                                {/* Reports Tab */}
                                <TabPanel value={tabValue} index={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ bgcolor: rgba(palette.info, 0.12), color: palette.info, p: 1, borderRadius: 2, display: 'flex' }}>
                                                    <AssessmentIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="h6" fontWeight={700} sx={{ color: palette.ink }}>
                                                        Generated Reports
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: palette.inkLight }}>{reports.length} reports available</Typography>
                                                </Box>
                                            </Box>
                                            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                                                <Button
                                                    variant="contained"
                                                    disableElevation
                                                    startIcon={<AddIcon />}
                                                    onClick={() => setOpenReportDialog(true)}
                                                    sx={{
                                                        borderRadius: '12px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1,
                                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                        boxShadow: '0 8px 24px -8px rgba(199,91,46,0.45)',
                                                        '&:hover': {
                                                            boxShadow: '0 12px 32px -8px rgba(199,91,46,0.55)',
                                                            transform: 'translateY(-2px)',
                                                        }
                                                    }}
                                                >
                                                    Generate Report
                                                </Button>
                                            </motion.div>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                            {reports.length === 0 ? (
                                                <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
                                                    <GlassCard accent={palette.inkLight} hover={false} elevation="md">
                                                        <AssessmentIcon sx={{ fontSize: 56, color: palette.inkLight, opacity: 0.3, mb: 2 }} />
                                                        <Typography variant="h6" sx={{ color: palette.ink, fontFamily: fontDisplay }}>
                                                            No reports generated yet
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: palette.inkLight, mt: 1 }}>
                                                            Generate your first report from real Firebase data
                                                        </Typography>
                                                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} style={{ display: 'inline-block', marginTop: 24 }}>
                                                            <Button 
                                                                variant="contained" 
                                                                disableElevation
                                                                startIcon={<AddIcon />} 
                                                                onClick={() => setOpenReportDialog(true)} 
                                                                sx={{ 
                                                                    borderRadius: '12px', 
                                                                    textTransform: 'none',
                                                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                                    boxShadow: '0 8px 24px -8px rgba(199,91,46,0.45)',
                                                                    '&:hover': {
                                                                        boxShadow: '0 12px 32px -8px rgba(199,91,46,0.55)',
                                                                        transform: 'translateY(-2px)',
                                                                    }
                                                                }}
                                                            >
                                                                Generate Report
                                                            </Button>
                                                        </motion.div>
                                                    </GlassCard>
                                                </Box>
                                            ) : (
                                                reports.map((report, index) => (
                                                    <Box key={report.id} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 10px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0, maxWidth: '100%' }}>
                                                        <GlassCard 
                                                            accent={report.status === 'completed' ? palette.pine : report.status === 'processing' ? palette.warning : palette.danger} 
                                                            delay={index * 0.05}
                                                            elevation="md"
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ color: palette.ink }}>
                                                                            {report.title}
                                                                        </Typography>
                                                                        <Chip 
                                                                            label={report.type} 
                                                                            size="small" 
                                                                            sx={{ 
                                                                                textTransform: 'capitalize',
                                                                                fontWeight: 600,
                                                                                height: 20,
                                                                                fontSize: '0.6rem',
                                                                                bgcolor: `${palette.teal}12`,
                                                                                color: palette.teal,
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Typography variant="caption" sx={{ color: palette.inkLight, display: 'block', mt: 0.5 }}>
                                                                        {report.description}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                                                        <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                                                            {report.date}
                                                                        </Typography>
                                                                        <Chip 
                                                                            icon={report.status === 'completed' ? <CheckCircleIcon /> : 
                                                                                  report.status === 'processing' ? <CircularProgress size={12} /> : 
                                                                                  <CancelIcon />}
                                                                            label={report.status} 
                                                                            size="small" 
                                                                            sx={{ 
                                                                                color: report.status === 'completed' ? palette.success : 
                                                                                       report.status === 'processing' ? palette.warning : 
                                                                                       palette.danger,
                                                                                fontWeight: 600,
                                                                                height: 20,
                                                                                fontSize: '0.6rem',
                                                                                bgcolor: report.status === 'completed' ? rgba(palette.success, 0.12) : 
                                                                                        report.status === 'processing' ? rgba(palette.warning, 0.12) : 
                                                                                        rgba(palette.danger, 0.12),
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ color: palette.inkLight }}>
                                                                            {report.size}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}>
                                                                    <Tooltip title="View Report">
                                                                        <IconButton size="small" sx={{ color: palette.info }}>
                                                                            <VisibilityIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Download">
                                                                        <IconButton size="small" sx={{ color: palette.rust }}>
                                                                            <DownloadIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Share">
                                                                        <IconButton size="small" sx={{ color: palette.purple }}>
                                                                            <ShareIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </GlassCard>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                    </Box>
                                </TabPanel>
                            </Box>
                        </GlassCard>
                    </>
                )}
            </Container>

            {/* Report Dialog */}
            <Dialog
                open={openReportDialog}
                onClose={() => setOpenReportDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            ...liquidGlass(palette.rust, 0.08, 'lg'),
                            borderRadius: '24px',
                            maxWidth: 500,
                            width: 'calc(100% - 24px)',
                            maxHeight: 'calc(100vh - 24px)',
                            m: 1.5,
                            p: 2,
                            '&::after': sheenLayer(palette.rust),
                        }
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: fontDisplay, fontWeight: 700, color: palette.ink }}>
                    Generate Report
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                    <Typography sx={{ color: palette.inkSoft, mb: 2 }}>
                        Select the type of report you want to generate from real Firebase data.
                    </Typography>
                    <Stack spacing={2}>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => { generateReport('full'); }}
                                sx={{
                                    borderRadius: '12px',
                                    justifyContent: 'flex-start',
                                    py: 1.5,
                                    borderColor: palette.hairline,
                                    '&:hover': { borderColor: palette.rust, bgcolor: rgba(palette.rust, 0.04) }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                                    <AssessmentIcon sx={{ color: palette.rust }} />
                                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>Complete System Report</Typography>
                                        <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>All metrics in one comprehensive report</Typography>
                                    </Box>
                                </Box>
                            </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => { generateReport('meeting'); }}
                                sx={{
                                    borderRadius: '12px',
                                    justifyContent: 'flex-start',
                                    py: 1.5,
                                    borderColor: palette.hairline,
                                    '&:hover': { borderColor: palette.purple, bgcolor: rgba(palette.purple, 0.04) }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                                    <EventIcon sx={{ color: palette.purple }} />
                                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>Meeting Analytics Report</Typography>
                                        <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Detailed meeting statistics and trends</Typography>
                                    </Box>
                                </Box>
                            </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => { generateReport('user'); }}
                                sx={{
                                    borderRadius: '12px',
                                    justifyContent: 'flex-start',
                                    py: 1.5,
                                    borderColor: palette.hairline,
                                    '&:hover': { borderColor: palette.teal, bgcolor: rgba(palette.teal, 0.04) }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                                    <PeopleIcon sx={{ color: palette.teal }} />
                                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>User Engagement Report</Typography>
                                        <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>User growth and engagement metrics</Typography>
                                    </Box>
                                </Box>
                            </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => { generateReport('revenue'); }}
                                sx={{
                                    borderRadius: '12px',
                                    justifyContent: 'flex-start',
                                    py: 1.5,
                                    borderColor: palette.hairline,
                                    '&:hover': { borderColor: palette.gold, bgcolor: rgba(palette.gold, 0.04) }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                                    <MoneyIcon sx={{ color: palette.gold }} />
                                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>Revenue Report</Typography>
                                        <Typography variant="caption" color={palette.inkLight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Revenue trends and financial insights</Typography>
                                    </Box>
                                </Box>
                            </Button>
                        </motion.div>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenReportDialog(false)} sx={{ color: palette.inkLight, fontWeight: 600 }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Analytics;